<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('settings.settingsLabel') }}</span>
        <h3 class="page-title">{{ $t('settings.customerContactTypes.addNewCustomerContactType') }}</h3>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push('/ustawienia/typy-kontaktow-z-klientem')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          {{ $t('ui.backToList') }}
        </d-button>

        <d-button
          theme="success"
          @click.prevent="validateForm">
          {{ $t('settings.customerContactTypes.editCustomerContactType') }}
        </d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form>
      <d-card class="mb-5">
        <d-tabs v-sticky-tabs card>
          <!-- Dane ogólne -->
          <d-tab
            :title="$t('ui.generalInfo')"
            active>
            <h4 class="mb-3 pb-3 border-bottom">
              {{ $t('ui.generalInfo') }}
            </h4>

            <validation-observer ref="customer-contact-type-edit-form" tag="form">
              <div class="form-group">
                <label for="name">
                  {{ $t('settings.customerContactTypes.name') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="name"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="name" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="type">
                  {{ $t('settings.customerContactTypes.type') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="type"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    :disabled="true"
                    v-model="type" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label>
                  {{ $t('settings.customerContactTypes.icon') }}:
                </label>
                <div v-if="icon_url">
                  <img :src="icon_url" />
                </div>

                <div v-if="icon_url">
                  <a
                    href="#"
                    @click.prevent="removeIcon()"
                    class="text-danger">
                    <small>{{ $t('settings.customerContactTypes.removeIcon') }}</small>
                  </a>
                </div>

                <file-input
                  v-if="!icon_url"
                  :onChange="setIcon"
                  id="inputFileIcon" />
              </div>
            </validation-observer>
          </d-tab>

          <d-tab :title="$t('settings.customerContactTypes.translations')">
            <h4 class="pb-3 mb-0 border-bottom d-flex">
              {{ $t('settings.transaltion.translations') }}

              <d-button
                class="ml-auto"
                theme="success"
                @click.prevent="addTranslation">
                <i class="material-icons">add</i>
                {{ $t('settings.transaltion.addTranslation') }}
              </d-button>
            </h4>

            <table class="table mt-2 mb-0 full-width">
              <thead class="bg-light">
                <tr>
                  <th scope="col" class="border-0" style="width: 150px;">{{ $t('ui.language') }}</th>
                  <th scope="col" class="border-0" style="width: calc(100% - 350px);">{{ $t('settings.customerContactTypes.name') }}</th>
                  <th scope="col" class="border-0" style="width: 200px;">{{ $t('ui.actions') }}</th>
                </tr>
              </thead>
              <tbody v-if="type_translations.length">
                <tr
                  v-for="(translation, index) of type_translations"
                  :key="'price-' + index">
                  <td class="align-middle">
                    {{ getLanguageByID(translation.language_id) }}
                  </td>
                  <td class="align-middle">
                    {{ translation.name }}
                  </td>
                  <td class="align-middle">
                    <d-button
                      theme="primary"
                      @click.prevent="editTranslation(translation)"
                      class="mr-2"
                      :title="$t('ui.edit')">
                      <i class="material-icons">edit</i>
                    </d-button>
                    <d-button
                      theme="danger"
                      :disabled="removeTranslationInProgress"
                      @click.prevent="removeTranslation(translation.id)"
                      :title="$t('ui.delete')">
                      <i class="material-icons">delete</i>
                    </d-button>
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="3" class="text-center">
                    {{ $t('settings.customerContactTypes.noTranslationsForThisCustomerContactType') }}&hellip;
                  </td>
                </tr>
              </tbody>
            </table>
          </d-tab>
        </d-tabs>

        <div v-if="!loadedData" class="pb-3 pt-3 text-center overlay-loading">
          {{ $t('ui.dataLoading') }}&hellip;
        </div>

        <d-alert
          v-if="loadError"
          show
          theme="warning">
          {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
        </d-alert>
      </d-card>
    </d-form>

    <customer-contact-type-translation-new-popup
      v-if="showAddTranslationPopup"
      :contactTypeID="this.id"
      :languages="languages" />

    <customer-contact-type-translation-edit-popup
      v-if="showEditTranslationPopup"
      :contactTypeID="this.id"
      :translationData="selectedTranslation"
      :languages="languages" />
  </d-container>
</template>

<script>
import FormUtils from './../../utils/FormUtils.js';
import ListUtils from './../../utils/ListUtils.js';
import CustomerContactTypeTranslationNewPopup from './CustomerContactTypeTranslationNewPopup';
import CustomerContactTypeTranslationEditPopup from './CustomerContactTypeTranslationEditPopup';

export default {
  name: 'customer-contact-type-edit',
  components: {
    'customer-contact-type-translation-new-popup': CustomerContactTypeTranslationNewPopup,
    'customer-contact-type-translation-edit-popup': CustomerContactTypeTranslationEditPopup
  },
  data () {
    return {
      icon: '',
      icon_url: '',
      name: '',
      type: '',
      type_translations: [],
      // flags
      loadedData: false,
      loadError: false,
      languages: [],
      loadedLanguages: false,
      showAddTranslationPopup: false,
      showEditTranslationPopup: false,
      selectedTranslation: 0,
      removeTranslationInProgress: false
    };
  },
  mounted () {
    FormUtils.loadItemData(this, {
      endpointBase: '/customer-contact-types/item/',
      instanceFields: ['name', 'type', 'icon', 'icon_url', 'type_translations'],
      loadedKey: 'loadedData',
      params: {
        with: ['typeTranslations']
      }
    });

    this.loadLanguages();
    this.$bus.$on('customer-contact-type-translation-new-popup-close', this.closeAddTranslationPopup);
    this.$bus.$on('customer-contact-type-translation-edit-popup-close', this.closeEditTranslationPopup);
    this.$bus.$on('customer-contact-type-reload-translations', this.loadTranslations);
  },
  methods: {
    validateForm () {
      FormUtils.validate(this.$refs['customer-contact-type-edit-form'], this.submitForm);
    },
    setIcon (event) {
      this.icon = event.target.files[0];
    },
    removeIcon () {
      this.icon_url = '';
      this.icon = '';
    },
    submitForm () {
      let imageFields = false;

      if (this.icon) {
        imageFields = ['icon'];
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/customer-contact-types/update/' + parseInt(this.$route.params.id, 10),
        formData: {
          icon: this.icon,
          name: this.name,
          type: this.type
        },
        successTitle: this.$t('settings.customerContactTypes.editCustomerContactTypeSuccessTitle'),
        successText: this.$t('settings.customerContactTypes.editCustomerContactTypeSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('settings.customerContactTypes.editCustomerContactTypeErrorText'),
        successAction: () => {
          this.$router.push('/ustawienia/typy-kontaktow-z-klientem/')
        }
      }, imageFields);
    },
    loadLanguages () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/languages/items',
        loadedKey: 'loadedLanguages',
        outputKey: 'languages',
        errorKey: 'loadError'
      });
    },
    getLanguageByID (languageID) {
      let filteredLanguages = this.languages.filter(language => language.id === languageID);

      if (filteredLanguages.length) {
        return filteredLanguages[0].name;
      }

      return '';
    },
    loadTranslations () {
      FormUtils.loadItemData(this, {
        endpointBase: '/customer-contact-types/item/',
        instanceFields: ['type_translations'],
        loadedKey: null,
        params: {
          with: ['typeTranslations']
        }
      });
    },
    addTranslation () {
      this.showAddTranslationPopup = true;
    },
    editTranslation (translationData) {
      this.selectedTranslation = JSON.parse(JSON.stringify(translationData));
      this.showEditTranslationPopup = true;
    },
    closeAddTranslationPopup () {
      this.showAddTranslationPopup = false;
    },
    closeEditTranslationPopup () {
      this.showEditTranslationPopup = false;
    },
    removeTranslation (translationID) {
      this.removeTranslationInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/customer-contact-types/translations/delete/',
        id: translationID,
        successTitle: this.$t('settings.transaltion.deleteTranslationSuccessTitle'),
        successText: this.$t('settings.transaltion.deleteTranslationSuccessText'),
        confirmTitle: this.$t('settings.transaltion.deleteTranslationConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('settings.transaltion.deleteTranslationErrorText'),
        successAction: () => {
          this.removeTranslationInProgress = false;
          this.loadTranslations();
        },
        cancelAction: () => {
          this.removeTranslationInProgress = false;
        }
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('customer-contact-type-translation-new-popup-close', this.closeAddTranslationPopup);
    this.$bus.$off('customer-contact-type-translation-edit-popup-close', this.closeEditTranslationPopup);
    this.$bus.$off('customer-contact-type-reload-translations', this.loadTranslations);
  }
};
</script>
